/* -------------------------- Design imports start -------------------------- */
import { Card, CardContent, Typography } from '@mui/material'
import React, { type ReactElement, useMemo } from "react";
import { type FileWithPath, useDropzone } from "react-dropzone";
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import LogTool from '../../../logger/logTools'
import LinearProgressWithLabel from '../../../components/widgets/LinearProgressWithLabel'
import { useUploadFileToGeneralFiles } from '../hooks/useFilesystemMutations'
/* ------------------------- Functional imports end ------------------------- */

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  borderWidth: 2,
  height: '148px',
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
}

const focusedStyle = {
  borderColor: '#16a9e4',
}

const acceptStyle = {
  borderColor: '#00e676',
}

const rejectStyle = {
  borderColor: '#ff1744',
}

type Props = {
  id?: string
  uploadPath?: string
  fetchFiles?: (path: string) => Promise<void>
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function S3FileUpload(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  // hook for upload that uses drag and drop
  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, acceptedFiles } =
    useDropzone({ onDrop: (acceptedFiles: FileWithPath[]) => {
      if (acceptedFiles.length > 0) {
        void (async () => {
          await uploadFiles(acceptedFiles);
        })()
      }
    }})
  const log = new LogTool({ context: 'S3FileUpload', enable: true, logLevel: 'warn' })
  const uploadFileMutation = useUploadFileToGeneralFiles()
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  /* ----------------------------- Flag states end ---------------------------- */
  /* ---------------------------- Data states start --------------------------- */
  const { t } = useTranslation()
  const loading = uploadFileMutation.isPending
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------- Utility functions start ------------------------ */
  /**
   * Handles uploading one or multiple files
   * @params files list of files to upload
   */
  async function uploadFiles(files: FileWithPath[]): Promise<void> {
    try {
      const uploadPath = props.uploadPath || ''
      const uploadPromises = files.map(file =>
        uploadFileMutation.mutateAsync({ path: uploadPath, file })
      )
      await Promise.all(uploadPromises)
      toast.success(t('common:feedback.success.uploadSuccess'))
      if (props.fetchFiles) {
        await props.fetchFiles(uploadPath)
      }
    } catch (error) {
      toast.error(t('common:feedback.error.uploadError'))
      log.error(error)
    }
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  )
  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography>{t('files:content.label.uploadFiles')}</Typography>
      </CardContent>
      <div className="container" style={{ margin: '5px' }}>
        <div {...getRootProps({ style })}>
          <input {...getInputProps()} />
          <Typography sx={{ color: '#666565', fontWeight: 'bold' }}>
            {t('common:content.label.dragAndDrop')}
          </Typography>
        </div>
        {loading && <LinearProgressWithLabel value={100} />}
        {/* <FileList type={'DOCUMENT'} fileKeys={fileKeys} filePath={filePath as string} /> */}
      </div>
    </Card>
  )
}
